import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { RefreshCw, Eye, Check, X } from 'lucide-react';
import { v3Api } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import './workpermit.css';

const statusClass = (s) => (s === 'APPROVED' ? 'approved' : s === 'REJECTED' ? 'rejected' : 'pending');

export default function WorkPermitApprovals() {
    const { t } = useTranslation();
    const { concessionaireId } = useAuth();

    const [permits, setPermits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState(null);          // summary object for the open drawer
    const [busyId, setBusyId] = useState(null);
    const [rejectTarget, setRejectTarget] = useState(null); // permit being rejected
    const [reason, setReason] = useState('');

    const load = useCallback(() => {
        if (!concessionaireId) return;
        setLoading(true);
        v3Api.workPermit.approvals()
            .then((rows) => setPermits(Array.isArray(rows) ? rows : []))
            .catch((e) => toast.error(e.response?.data?.message || t('Failed to load work permits')))
            .finally(() => setLoading(false));
    }, [concessionaireId, t]);

    useEffect(() => { load(); }, [load]);

    const openDetail = async (id) => {
        try {
            const summary = await v3Api.workPermit.summary(id);
            setDetail(summary);
        } catch (e) {
            toast.error(e.response?.data?.message || t('Failed to load details'));
        }
    };

    const approve = async (id) => {
        setBusyId(id);
        try {
            const res = await v3Api.workPermit.approve(id);
            toast.success(res.message || t('Approval recorded'));
            load();
        } catch (e) {
            toast.error(e.response?.data?.message || t('Failed to approve'));
        } finally {
            setBusyId(null);
        }
    };

    const submitReject = async () => {
        if (!rejectTarget) return;
        setBusyId(rejectTarget);
        try {
            const res = await v3Api.workPermit.reject(rejectTarget, reason);
            toast.success(res.message || t('Work permit rejected'));
            setRejectTarget(null);
            setReason('');
            load();
        } catch (e) {
            toast.error(e.response?.data?.message || t('Failed to reject'));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="v3 v3-wp">
            <div className="v3-wp-header">
                <div>
                    <h1 className="v3-wp-title">{t('Work Permit Requests')}</h1>
                    <div className="v3-wp-subtitle">{t('Review and act on permits awaiting your approval.')}</div>
                </div>
                <div className="v3-wp-actions">
                    <button className="v3-btn v3-btn-ghost" onClick={load} disabled={loading}>
                        <RefreshCw size={14} className={loading ? 'v3-wp-spin' : ''} /> {t('Refresh')}
                    </button>
                </div>
            </div>

            <div className="v3-wp-card">
                <table className="v3-wp-table">
                    <thead>
                        <tr>
                            <th>{t('Store')}</th><th>{t('Type')}</th><th>{t('Created')}</th><th>{t('Status')}</th><th>{t('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {permits.length === 0 ? (
                            <tr><td colSpan="5" className="v3-wp-empty">{t('No Work Permits Found')}</td></tr>
                        ) : permits.map((wp) => (
                            <tr key={wp._id}>
                                <td>{wp.tenant_information?.store || '—'}</td>
                                <td>{wp.workpermit_type}</td>
                                <td>{new Date(wp.createdAt).toLocaleString()}</td>
                                <td><span className={`v3-wp-pill ${statusClass(wp.status)}`}>{wp.status}</span></td>
                                <td style={{ display: 'flex', gap: 8 }}>
                                    <button className="v3-btn" onClick={() => openDetail(wp._id)}><Eye size={14} /> {t('View')}</button>
                                    <button className="v3-btn v3-btn-primary" disabled={busyId === wp._id || wp.status !== 'PENDING'} onClick={() => approve(wp._id)}>
                                        <Check size={14} /> {t('Approve')}
                                    </button>
                                    <button className="v3-btn" disabled={busyId === wp._id || wp.status !== 'PENDING'} onClick={() => { setRejectTarget(wp._id); setReason(''); }}>
                                        <X size={14} /> {t('Reject')}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Detail drawer */}
            {detail && (
                <div className="v3-wp-overlay" onClick={() => setDetail(null)}>
                    <div className="v3-wp-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="v3-wp-modal-head">
                            <div>
                                <h5 className="v3-wp-title" style={{ fontSize: 18 }}>{detail.workpermit_type}</h5>
                                <div className="v3-wp-muted">{t('Created')}: {detail.createdAt ? new Date(detail.createdAt).toLocaleString() : '—'}</div>
                            </div>
                            <button className="v3-wp-iconbtn" onClick={() => setDetail(null)}>✕</button>
                        </div>

                        <div className="v3-wp-detail-section">
                            <h6>{t('Tenant Information')}</h6>
                            <dl className="v3-wp-detail-grid">
                                {Object.entries(detail.tenant_information || {}).map(([k, v]) => (
                                    <React.Fragment key={k}><dt>{k}</dt><dd>{String(v || '—')}</dd></React.Fragment>
                                ))}
                            </dl>
                        </div>

                        {(detail.vehicle_details || []).length > 0 && (
                            <div className="v3-wp-detail-section">
                                <h6>{t('Vehicle Details')}</h6>
                                {detail.vehicle_details.map((v, i) => (
                                    <div className="v3-wp-muted" key={i}>{v.vehicle_name} · {v.plate_number} · {v.driver_name} · {v.time_slot}</div>
                                ))}
                            </div>
                        )}

                        {(detail.worker_details || []).length > 0 && (
                            <div className="v3-wp-detail-section">
                                <h6>{t('Worker Details')}</h6>
                                {detail.worker_details.map((w, i) => (
                                    <div className="v3-wp-muted" key={i}>{w.name} · {w.nationality} · {w.number}</div>
                                ))}
                            </div>
                        )}

                        {(detail.contractor_details || []).length > 0 && (
                            <div className="v3-wp-detail-section">
                                <h6>{t('Contractor Details')}</h6>
                                {detail.contractor_details.map((c, i) => (
                                    <div className="v3-wp-muted" key={i}>{c.name} ({c.company}) · {c.number} · {c.email}</div>
                                ))}
                            </div>
                        )}

                        <div className="v3-wp-detail-section">
                            <h6>{t('Multi-Level Approvals')}</h6>
                            {(detail.multi_level_approvals || []).map((m, i) => (
                                <div key={i} className="v3-wp-muted">
                                    <strong style={{ color: 'var(--v3-text-primary)' }}>{m.user_role}</strong> — {m.approved_status}
                                    {m.rejection_reason ? ` (${m.rejection_reason})` : ''}
                                </div>
                            ))}
                        </div>

                        <div className="v3-wp-rowfoot" style={{ marginTop: 18 }}>
                            <button className="v3-btn" onClick={() => setDetail(null)}>{t('Close')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject reason modal */}
            {rejectTarget && (
                <div className="v3-wp-overlay" onClick={() => setRejectTarget(null)}>
                    <div className="v3-wp-modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
                        <div className="v3-wp-modal-head">
                            <h5 className="v3-wp-title" style={{ fontSize: 16 }}>{t('Reject Work Permit')}</h5>
                            <button className="v3-wp-iconbtn" onClick={() => setRejectTarget(null)}>✕</button>
                        </div>
                        <div className="v3-wp-field">
                            <label className="v3-wp-label">{t('Rejection Reason')}</label>
                            <textarea className="v3-wp-textarea" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('Reason for rejection')} />
                        </div>
                        <div className="v3-wp-rowfoot" style={{ marginTop: 16 }}>
                            <button className="v3-btn" onClick={() => setRejectTarget(null)}>{t('Cancel')}</button>
                            <button className="v3-btn v3-btn-primary" onClick={submitReject} disabled={busyId === rejectTarget}>{t('Confirm Reject')}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
